import { useMutation } from '@tanstack/react-query';
import {
  QuoteDTO,
  QuoteItemDTO,
  updateQuoteV2SectionMutation
} from '@setvi/shared/services/react-query';
import { getItemId } from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

interface UpdateSectionVariables {
  sectionId: string;
  name: string;
  description?: string;
}

/**
 * Update section optimistically in quote
 */
const updateSectionOptimistically = (
  quote: QuoteDTO | undefined,
  { sectionId, name, description }: UpdateSectionVariables
): QuoteDTO | undefined => {
  if (!quote) return quote;

  const updateInList = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
    items.map(item => {
      if (getItemId(item) === sectionId && item.type === 'section') {
        return {
          ...item,
          name,
          note: description || null
        };
      }

      return item;
    });

  return {
    ...quote,
    items: updateInList(quote.items)
  };
};

/**
 * Update section title and description using v2 section endpoint
 * Changes appear immediately in UI before API confirmation
 */
export const useUpdateSection = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = updateQuoteV2SectionMutation();

  return useMutation({
    mutationFn: ({ sectionId, name, description }: UpdateSectionVariables) =>
      mutationFn({
        quoteId,
        sectionId,
        name,
        note: description || ''
      }),

    onMutate: async (variables: UpdateSectionVariables) => {
      await cache.cancel();

      const previousQuote = cache.getQuote();
      const previousItems = cache.getItems();

      const composed = previousQuote
        ? ({
            ...previousQuote,
            items: previousItems || []
          } as unknown as QuoteDTO)
        : undefined;

      const updated = updateSectionOptimistically(composed, variables);

      if (updated) {
        const { items: updatedItems, ...quoteData } = updated as any;
        cache.setQuote(() => quoteData);
        cache.setItems(() => updatedItems);
      }

      return { previousQuote, previousItems };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: () => {
      cache.invalidate();
    }
  });
};
